import React, { useEffect, useRef, useState, useCallback } from "react";
import { readConsent, writeConsent, loadTracking } from "../../utils/consent";

// Ticket #557 - Cookie-Consent-Banner
// Granular categories: Funktional (always on), Analyse, Marketing
// Persists to localStorage + best-effort POST /consent
// Re-consent prompt after 12 months
// Reopens via window event "open-cookie-settings"
// Keyboard accessible, focus-trapped, ESC = "Nur nötig"

const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [analyse, setAnalyse] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const containerRef = useRef(null);
  const firstFocusRef = useRef(null);

  // Initial mount: show only when no valid consent exists.
  // The banner is intentionally deferred past first paint so the landing
  // hero remains the LCP element (Lighthouse mobile is otherwise dominated
  // by the modal banner painting full-width text).
  useEffect(() => {
    const existing = readConsent();
    if (!existing) {
      const reveal = () => setVisible(true);
      const idle =
        typeof window !== "undefined" && "requestIdleCallback" in window
          ? window.requestIdleCallback
          : (cb) => setTimeout(cb, 1500);
      const handle = idle(reveal, { timeout: 2500 });
      return () => {
        if (
          typeof window !== "undefined" &&
          "cancelIdleCallback" in window &&
          typeof handle === "number"
        ) {
          window.cancelIdleCallback(handle);
        } else {
          clearTimeout(handle);
        }
      };
    } else {
      // Honor stored prefs and load any gated trackers
      setAnalyse(!!existing.analyse);
      setMarketing(!!existing.marketing);
      loadTracking();
      return undefined;
    }
  }, []);

  // Listen for the global reopen event (fired by Footer "Cookie-Einstellungen")
  useEffect(() => {
    const handler = () => {
      const existing = readConsent();
      if (existing) {
        setAnalyse(!!existing.analyse);
        setMarketing(!!existing.marketing);
      }
      setShowSettings(true);
      setVisible(true);
    };
    window.addEventListener("open-cookie-settings", handler);
    return () => window.removeEventListener("open-cookie-settings", handler);
  }, []);

  const acceptOnlyNecessary = useCallback(() => {
    writeConsent({ analyse: false, marketing: false });
    setVisible(false);
    setShowSettings(false);
    loadTracking();
  }, []);

  const acceptAll = useCallback(() => {
    writeConsent({ analyse: true, marketing: true });
    setVisible(false);
    setShowSettings(false);
    loadTracking();
  }, []);

  const saveSelection = useCallback(() => {
    writeConsent({ analyse, marketing });
    setVisible(false);
    setShowSettings(false);
    loadTracking();
  }, [analyse, marketing]);

  // Focus trap + ESC handling
  useEffect(() => {
    if (!visible) return undefined;

    // Focus the first interactive element after render
    const t = setTimeout(() => {
      firstFocusRef.current?.focus();
    }, 30);

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        acceptOnlyNecessary();
        return;
      }
      if (e.key === "Tab" && containerRef.current) {
        const focusables = containerRef.current.querySelectorAll(
          'button, [href], input, [tabindex]:not([tabindex="-1"])'
        );
        const enabled = Array.from(focusables).filter(
          (el) => !el.disabled && el.offsetParent !== null
        );
        if (enabled.length === 0) return;
        const first = enabled[0];
        const last = enabled[enabled.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      clearTimeout(t);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [visible, acceptOnlyNecessary]);

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="cookie-banner-title"
      aria-describedby="cookie-banner-desc"
      className="fixed inset-x-0 bottom-0 z-[60] flex justify-center px-4 pb-4 sm:px-6 sm:pb-6"
    >
      <div
        ref={containerRef}
        className="w-full max-w-3xl rounded-2xl bg-white shadow-2xl ring-1 ring-gray-200 p-6 sm:p-8"
        style={{ fontFamily: "Plus Jakarta Sans, sans-serif" }}
      >
        <h2
          id="cookie-banner-title"
          className="text-xl sm:text-2xl font-bold text-gray-900"
        >
          Cookie-Einstellungen
        </h2>
        <p
          id="cookie-banner-desc"
          className="mt-2 text-sm sm:text-base text-gray-700 leading-relaxed"
        >
          Wir verwenden Cookies, damit Reflecta funktioniert, um die Nutzung zu
          analysieren und um Inhalte auszuspielen. Funktionale Cookies sind
          notwendig. Du kannst Analyse und Marketing einzeln zulassen oder
          ablehnen. Deine Auswahl gilt 12 Monate und kann jederzeit über
          "Cookie-Einstellungen" im Footer angepasst werden.
        </p>

        {showSettings && (
          <div className="mt-5 space-y-3">
            <CategoryRow
              title="Funktional"
              description="Notwendig für den Betrieb der Seite (z. B. Login, Sicherheit). Immer aktiv."
              checked={true}
              disabled={true}
              onChange={() => {}}
            />
            <CategoryRow
              title="Analyse"
              description="Hilft uns zu verstehen, wie Reflecta genutzt wird, damit wir das Produkt verbessern können."
              checked={analyse}
              disabled={false}
              onChange={(v) => setAnalyse(v)}
            />
            <CategoryRow
              title="Marketing"
              description="Wird genutzt, um relevante Inhalte und Angebote auszuspielen."
              checked={marketing}
              disabled={false}
              onChange={(v) => setMarketing(v)}
            />
          </div>
        )}

        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
          {!showSettings && (
            <button
              ref={firstFocusRef}
              type="button"
              onClick={() => setShowSettings(true)}
              className="rounded-xl px-5 py-2.5 text-sm font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              Einstellungen
            </button>
          )}
          <button
            type="button"
            onClick={acceptOnlyNecessary}
            className="rounded-xl px-5 py-2.5 text-sm font-medium text-gray-800 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            Nur nötig
          </button>
          {showSettings ? (
            <button
              type="button"
              onClick={saveSelection}
              className="rounded-xl px-5 py-2.5 text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-purple-300"
              style={{
                background: "linear-gradient(135deg, #9B30FF 0%, #6A0DAD 100%)",
              }}
            >
              Auswahl speichern
            </button>
          ) : (
            <button
              ref={showSettings ? null : firstFocusRef}
              type="button"
              onClick={acceptAll}
              className="rounded-xl px-5 py-2.5 text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-purple-300"
              style={{
                background: "linear-gradient(135deg, #9B30FF 0%, #6A0DAD 100%)",
              }}
            >
              Alle akzeptieren
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const CategoryRow = ({ title, description, checked, disabled, onChange }) => (
  <div className="flex items-start justify-between gap-4 rounded-xl bg-gray-50 p-4">
    <div className="min-w-0">
      <p className="text-sm font-semibold text-gray-900">{title}</p>
      <p className="mt-1 text-xs sm:text-sm text-gray-600 leading-relaxed">
        {description}
      </p>
    </div>
    <label className="relative inline-flex items-center cursor-pointer shrink-0 mt-1">
      <input
        type="checkbox"
        className="sr-only peer"
        checked={checked}
        disabled={disabled}
        aria-label={`${title} aktivieren`}
        onChange={(e) => onChange(e.target.checked)}
      />
      <div
        className={`w-11 h-6 rounded-full transition-colors ${
          disabled
            ? "bg-purple-300 cursor-not-allowed"
            : checked
            ? "bg-purple-600"
            : "bg-gray-300"
        } peer-focus:ring-2 peer-focus:ring-purple-500`}
      />
      <span
        className={`absolute top-0.5 left-0.5 bg-white w-5 h-5 rounded-full shadow transition-transform ${
          checked ? "translate-x-5" : ""
        }`}
      />
    </label>
  </div>
);

export default CookieBanner;
